import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { FormSection } from '../../../common/components/SectionedForm'
import { api, buildPaginatedUrl } from '../../../common/api'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { EnterpriseGbtfSite, VpRole, VpUser, VpUserProfile } from '../../../app/types'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import { Autocomplete, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, Radio, RadioGroup, TextField } from '@mui/material'
import { usePrivilege } from '../../../common/util/hooks'
import { FeaturePrivileges } from '../../../common/util/featurePrivileges'
import { ColumnDefinition, InfiniteScrollTable } from '../../../common/components/table/Table'
import TableRowActionsMenu from '../../../common/components/table/TableRowActionsMenu'
import { sorterHeader } from '../../../common/components/table/SorterHeader'
import { AddButton } from '../../../common/components/AddButton'
import CancelButton from '../../../common/form/CancelButton'
import ErrorAlert from '../../../common/components/ErrorAlert'
import DeletionModal from '../../../common/components/DeletionModal'
import { customerOriginIdSelector, meSelector } from '../../../authentication/redux'
import TextInput from '../../../common/form/TextInput'
import FormRow from '../../../common/form/FormRow'
import FormFieldBox from '../../../common/form/FormFieldBox'
import SpinnableSubmitButton from '../../../common/form/SpinnableSubmitButton'
import { useForm } from 'react-hook-form'

type ProfileFormData = {
    roleId: string
    siteMode: 'all' | 'specific'
    siteIds: string[]
}

export const useVpUserProfilesSection = (userId: string, user?: VpUser): FormSection<VpUserProfile[]> => {
    const { t } = useTranslation()
    const customerOriginId = useSelector(customerOriginIdSelector)
    const me = useSelector(meSelector)
    const { hasAccess: hasWriteAccess, onlyWithAccess: onlyWithWriteAccess } = usePrivilege(FeaturePrivileges.enterpriseAdminMgmt.write)

    const [profiles, setProfiles] = useState<VpUserProfile[]>([])
    const [isFetching, setIsFetching] = useState(false)
    const [error, setError] = useState<any>(null)
    const [roles, setRoles] = useState<VpRole[]>([])
    const [sites, setSites] = useState<EnterpriseGbtfSite[]>([])
    const [entryToDelete, setEntryToDelete] = useState<VpUserProfile | null>(null)
    const [entryToEdit, setEntryToEdit] = useState<VpUserProfile | null>(null)
    const [showCreateDialog, setShowCreateDialog] = useState(false)

    const fetchProfiles = useCallback(async () => {
        if (!customerOriginId || !userId) return
        try {
            setIsFetching(true)
            const { data } = await api.get(`/api/enterprise/${customerOriginId}/vpUsers/${userId}/profiles`)
            setProfiles(data)
            setError(null)
        } catch (e) {
            setError(e)
        } finally {
            setIsFetching(false)
        }
    }, [customerOriginId, userId])

    const fetchRoles = useCallback(async () => {
        if (!customerOriginId) return
        try {
            const { data } = await api.get(`/api/enterprise/${customerOriginId}/vpRoles?size=1000&sort=name,asc`)
            setRoles(data.content)
        } catch (e) { /* ignore */
        }
    }, [customerOriginId])

    const fetchSites = useCallback(async () => {
        if (!me?.voiceCloudProductId) return
        try {
            const { data } = await api.get(buildPaginatedUrl(`/api/enterprise/${me.voiceCloudProductId}/sites`, 0, { field: 'name', order: 'asc' }, null, 1000))
            setSites(data.content)
        } catch (e) { /* ignore */
        }
    }, [me])

    useEffect(() => {
        fetchProfiles()
        fetchRoles()
        fetchSites()
    }, [fetchProfiles, fetchRoles, fetchSites])

    const getSiteNames = useCallback((siteIds: string[]): string => {
        if (!siteIds || siteIds.length === 0) return t('admin-management.all-sites')
        return siteIds.map(id => {
            const site = sites.find(s => s.productId === id)
            return site ? site.name : id
        }).join(', ')
    }, [sites, t])

    const rowActions = useMemo(
        () => [
            {
                key: 'edit',
                text: t('general.edit'),
                onClick: (record: VpUserProfile) => {
                    setEntryToEdit(record)
                },
                dividerBeneath: true,
                needsWriteAccess: true,
            },
            {
                key: 'delete',
                text: t('general.delete'),
                onClick: (record: VpUserProfile) => {
                    setEntryToDelete(record)
                },
                needsWriteAccess: true,
            },
        ],
        [t],
    )

    // @ts-ignore
    const columns: ColumnDefinition<VpUserProfile>[] = useMemo(
        () => [
            {
                title: t('admin-management.profile-role'),
                key: 'roleName',
                sorter: false,
                render: (text: string) => <>{text}</>,
            },
            {
                title: t('admin-management.profile-sites'),
                key: 'siteIds',
                sorter: false,
                render: (siteIds: string[]) => <>{getSiteNames(siteIds)}</>,
            },
            {
                title: '',
                key: 'id',
                sorter: false,
                render: (_: string, record: VpUserProfile) => <TableRowActionsMenu record={record} menuItems={rowActions} readOnly={!hasWriteAccess} />,
            },
        ],
        [t, rowActions, hasWriteAccess, getSiteNames],
    )

    const getRowKey = useCallback(entry => entry.id, [])

    const getDeleteUrl = useCallback(
        (p: VpUserProfile | null) => p ? `/api/enterprise/${customerOriginId}/vpUsers/${userId}/profiles/${p.id}` : null,
        [customerOriginId, userId],
    )

    const buttonPanel = useMemo(
        () => onlyWithWriteAccess(
            <Grid container item justifyContent={'space-between'} flexDirection={'row'} spacing={2} flexWrap={'nowrap'}>
                <Grid item>
                    <AddButton
                        onClick={() => {
                            setShowCreateDialog(true)
                            return null
                        }}
                        label={`${t('admin-management.add-profile')}`}
                    />
                </Grid>
            </Grid>,
        ),
        [t, onlyWithWriteAccess],
    )

    const renderDetails = useCallback(
        (data: VpUserProfile[]): ReactNode => {
            return (
                <Grid container p={1}>
                    <Grid container xs={12} mb={3} item justifyContent={'flex-start'} flexDirection={'row'} flexWrap={'nowrap'}>
                        <Grid item>{buttonPanel}</Grid>
                    </Grid>
                    <ErrorAlert showAlert={!!error} />
                    <Grid item xs={12}>
                        <InfiniteScrollTable<VpUserProfile>
                            hasNextPage={false}
                            data={profiles}
                            fetchNextPage={() => Promise.resolve({} as any)}
                            isFetching={false}
                            getRowKey={getRowKey}
                            sorter={col => sorterHeader(col, null, () => {
                            })}
                            columns={columns}
                            tableId={'vpUserProfilesTable'}
                        />
                    </Grid>
                    <DeletionModal<VpUserProfile | null>
                        nameToDelete={p => p?.roleName || ''}
                        itemToDelete={entryToDelete} deleteUrl={getDeleteUrl} onClose={() => setEntryToDelete(null)} onSuccess={fetchProfiles} />
                    <ProfileFormDialog
                        open={showCreateDialog || !!entryToEdit}
                        profile={entryToEdit}
                        roles={roles}
                        sites={sites}
                        onClose={() => {
                            setShowCreateDialog(false)
                            setEntryToEdit(null)
                        }}
                        onSaved={fetchProfiles}
                        customerOriginId={customerOriginId}
                        userId={userId}
                    />
                </Grid>
            )
        },
        [profiles, buttonPanel, error, columns, getRowKey, entryToDelete, getDeleteUrl, fetchProfiles, showCreateDialog, entryToEdit, roles, sites, customerOriginId, userId],
    )

    return {
        title: t('admin-management.profiles-section'),
        data: profiles,
        isFetching: isFetching,
        error: error,
        refetch: fetchProfiles,
        renderDetails: renderDetails,
        renderForm: null,
        enabled: () => true,
        icon: () => <AdminPanelSettingsIcon />,
    }
}

// --- Profile create/edit dialog ---
type ProfileFormDialogProps = {
    open: boolean
    profile: VpUserProfile | null
    roles: VpRole[]
    sites: EnterpriseGbtfSite[]
    onClose: () => void
    onSaved: () => void
    customerOriginId: string | null
    userId: string
}

const ProfileFormDialog = ({ open, profile, roles, sites, onClose, onSaved, customerOriginId, userId }: ProfileFormDialogProps) => {
    const { t } = useTranslation()
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const isEdit = !!profile

    const { control, handleSubmit, reset, watch, setValue } = useForm<ProfileFormData>({
        defaultValues: {
            roleId: '',
            siteMode: 'all',
            siteIds: [],
        },
    })

    useEffect(() => {
        if (open && profile) {
            setValue('roleId', profile.roleId)
            setValue('siteMode', (!profile.siteIds || profile.siteIds.length === 0) ? 'all' : 'specific')
            setValue('siteIds', profile.siteIds || [])
        } else if (open) {
            reset({ roleId: '', siteMode: 'all', siteIds: [] })
        }
    }, [open, profile, setValue, reset])

    const siteMode = watch('siteMode')
    const selectedSiteIds = watch('siteIds')

    const selectedSiteObjects = useMemo(
        () => {
            const ids = selectedSiteIds || []
            return sites.filter(s => ids.includes(s.productId))
        },
        [sites, selectedSiteIds],
    )

    const onSubmitForm = useCallback(async (formData: ProfileFormData) => {
        if (!customerOriginId) return
        try {
            setSubmitting(true)
            setError(null)
            const payload = {
                roleId: formData.roleId,
                siteIds: formData.siteMode === 'all' ? [] : formData.siteIds,
            }
            if (isEdit && profile) {
                await api.put(`/api/enterprise/${customerOriginId}/vpUsers/${userId}/profiles/${profile.id}`, payload)
            } else {
                await api.post(`/api/enterprise/${customerOriginId}/vpUsers/${userId}/profiles`, payload)
            }
            onSaved()
            onClose()
        } catch (e) {
            setError('general.unknown-server-error')
        } finally {
            setSubmitting(false)
        }
    }, [customerOriginId, userId, isEdit, profile, onSaved, onClose])

    return (
        <Dialog disablePortal open={open} onClose={onClose} maxWidth='sm' fullWidth>
            <form onSubmit={handleSubmit(onSubmitForm)} noValidate>
                <DialogTitle>{t(isEdit ? 'admin-management.edit-profile' : 'admin-management.add-profile')}</DialogTitle>
                <DialogContent>
                    <ErrorAlert showAlert={!!error} errorKey={error ?? undefined} />
                    <FormRow>
                        <FormFieldBox fullWidth={true}>
                            <TextInput label={t('admin-management.profile-role')} name={'roleId'} control={control} required={true}
                                       options={roles.map(r => ({ value: r.id, label: r.name + (r.global ? ` (${t('admin-management.role-type-global')})` : '') }))} />
                        </FormFieldBox>
                    </FormRow>
                    <RadioGroup
                        value={siteMode}
                        onChange={(e) => {
                            setValue('siteMode', e.target.value as 'all' | 'specific')
                            if (e.target.value === 'all') setValue('siteIds', [])
                        }}
                        sx={{ mt: 2, mb: 1 }}
                    >
                        <FormControlLabel value='all' control={<Radio size='small' />} label={t('admin-management.all-sites')} />
                        <FormControlLabel value='specific' control={<Radio size='small' />} label={t('admin-management.specific-sites')} />
                    </RadioGroup>
                    {siteMode === 'specific' && (
                        <Autocomplete
                            multiple
                            size='small'
                            options={sites}
                            getOptionLabel={s => s.name}
                            value={selectedSiteObjects}
                            onChange={(_, value) => setValue('siteIds', value.map(s => s.productId))}
                            isOptionEqualToValue={(option, value) => option.productId === value.productId}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => <Chip variant='outlined' label={option.name} size='small' {...getTagProps({ index })} />)
                            }
                            renderInput={params => <TextField {...params} label={t('admin-management.profile-sites')} variant='outlined' />}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <SpinnableSubmitButton label={t(isEdit ? 'general.form-update' : 'general.form-create')} showSpinner={submitting} />
                    <CancelButton onClick={onClose} />
                </DialogActions>
            </form>
        </Dialog>
    )
}




